import {
  DetectionAlertGroupCountSummary,
  DetectionAlertGroupSummary,
  DetectionAlertSummary,
  DetectionAlertBucketSummary,
} from '@investigator/shared/generated/graphql';
import moment from 'moment';
import { intColor } from './chartOptions';

export interface SeriesItem {
  name?: string;
  data: ApexAxisChartSeries[] | [];
}

const emptyDataItem = {
  name: '',
  data: [],
} as SeriesItem;

export interface seriesDataArgs {
  seriesMaxLength: number;
  seriesData?: DetectionAlertGroupCountSummary;
}

interface SeriesDataReturn {
  row_count: number;
  series: SeriesItem[];
}

export const seriesData = ({
  seriesData,
  seriesMaxLength,
}: seriesDataArgs): SeriesDataReturn | null => {
  if (!seriesData) {
    return null;
  } else {
    const { summary, row_count } = seriesData;

    const series = [...new Array(seriesMaxLength)] as SeriesItem[];

    (summary ?? []).forEach(
      (summaryData: DetectionAlertGroupSummary | null | undefined, index) => {
        if (!summaryData) return;
        const severity =
          summaryData.severity != null ? String(summaryData.severity) : '';
        const alert_name = summaryData.alert_name ?? '';
        const entity_name = summaryData.entity_name ?? '';
        const detections = summaryData.detections ?? [];
        const summarySeries = (detections ?? [])
          .filter(Boolean)
          .map((detection: DetectionAlertSummary | null | undefined) => {
            if (!detection) return null;
            const detectionEarliest =
              (detection.earliest_start_timestamp ?? 0) * 1000;
            const detectionLatest =
              (detection.latest_start_timestamp ?? 0) * 1000;
            const totalAlerts = (detection.buckets ?? [])
              .filter(Boolean)
              .reduce(
                (
                  accumulator: number,
                  bucket: DetectionAlertBucketSummary | null | undefined,
                ) => accumulator + (bucket?.alert_count ?? 0),
                0,
              );
            const detection_id = detection.detection_id ?? '';
            return {
              x: `${severity} ${alert_name} ${entity_name}`,
              y: [detectionEarliest, detectionLatest],
              severity,
              alert_name,
              entity_name,
              detection_id,
              ...(moment(detectionLatest).diff(
                moment(detectionEarliest),
                'hours',
              ) > 1 && {
                goals: (detection.buckets ?? [])
                  .filter(Boolean)
                  .map(
                    (
                      bucket: DetectionAlertBucketSummary | null | undefined,
                    ) => {
                      if (!bucket) return null;
                      return {
                        name: '',
                        strokeColor: intColor(Number(severity) || 0),
                        detectionId: `${detection_id}`,
                        toolTipTextStart: `${moment((bucket.bucket_start ?? 0) * 1000).format('lll')}`,
                        toolTipTextEnd: `${moment((bucket.bucket_end ?? 0) * 1000).format('lll')}`,
                        toolTipTextAlerts: `${bucket.alert_count ?? 0} Alerts`,
                        value: [
                          (bucket.bucket_start ?? 0) * 1000,
                          (bucket.bucket_end ?? 0) * 1000,
                        ],
                      };
                    },
                  )
                  .filter(Boolean),
              }),
              ...(moment(detectionLatest).diff(
                moment(detectionEarliest),
                'hours',
              ) <= 1 && {
                goals: [
                  {
                    name: '',
                    strokeColor: intColor(Number(severity) || 0),
                    detectionId: `${detection_id}`,
                    toolTipTextStart: `${moment(detectionEarliest).format('lll')}`,
                    toolTipTextEnd: `${moment(detectionLatest).format('lll')}`,
                    toolTipTextAlertName: alert_name,
                    toolTipTextEntityName: entity_name,
                    toolTipTextAlerts: `${totalAlerts} Alerts`,
                    value:
                      (detection.earliest_start_timestamp ?? 0) * 1000 +
                      ((detection.latest_start_timestamp ?? 0) * 1000 -
                        (detection.earliest_start_timestamp ?? 0) * 1000) /
                        2,
                    strokeWidth: 12,
                    strokeHeight: 0,
                    strokeLineCap: 'round',
                  },
                ],
              }),
            };
          })
          .filter(Boolean);
        const formattedSummary = {
          name:
            severity && alert_name && entity_name
              ? `${severity} ${alert_name} ${entity_name}`
              : severity && alert_name
                ? `${severity} ${alert_name}`
                : severity
                  ? `${severity}`
                  : undefined,
          data: summarySeries,
        } as SeriesItem;
        series[index] = formattedSummary;
      },
    );

    const treatedSeries = [
      ...series.map((series) => {
        if (series === undefined) {
          return emptyDataItem;
        } else {
          return series;
        }
      }),
    ] as SeriesItem[];

    return {
      row_count: row_count ?? 0,
      series: treatedSeries,
    };
  }
};
